import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { formatISO, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import type { CommunityFeedTabsDto } from "@/api/types";
import { ALL_COMMUNITY_FEED_ITEM_TYPES } from "@/components/CommunityFeed/helpers";
import { ErrorPage } from "@/components/Error/ErrorPage";
import type { MessageRouteParams } from "@/helpers/community-feed";
import { parseQueryParamToDateString, parseQueryParamToList } from "@/helpers/query-params";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import type { ApiQueryParams } from "@/types/api-types";

import type { CommunityFeedFilterValues } from "../../components/CommunityFeedFilterDrawer";
import { ALL_COMMUNITY_FEED_TABS } from "../../constants";
import { CommunityFeed, type CommunityFeedProps } from "./CommunityFeed";

export function CommunityFeedPage(): React.ReactNode {
  const [queryParams, setQueryParams] = useQueryParams<MessageRouteParams["list"]>();
  const [search, setSearch] = useState(() => queryParams.search ?? "");
  const sessionUser = useSessionUser();
  const communityFeedQueries = useCommunityFeedQueries();
  const communityGroupQueries = useCommunityGroupQueries();

  const currFilter: CommunityFeedFilterValues = useMemo(() => {
    return {
      startDate: parseQueryParamToDateString(queryParams.startDate),
      endDate: parseQueryParamToDateString(queryParams.endDate),
      groupIds: parseQueryParamToList(queryParams.groupIds),
    };
  }, [queryParams.startDate, queryParams.endDate, queryParams.groupIds]);
  const activeTab: CommunityFeedTabsDto = useMemo(() => {
    if (queryParams.tab && ALL_COMMUNITY_FEED_TABS.includes(queryParams.tab)) {
      return queryParams.tab;
    }

    return sessionUser.isAdmin ? "all" : "myFeed";
  }, [queryParams.tab, sessionUser.isAdmin]);
  const normalizedSearch = search.trim().toLowerCase();
  const debouncedQuery = useDebounceValue(normalizedSearch.length < 2 ? "" : normalizedSearch);

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);

  const handleUpdateFilter = (newFilter: CommunityFeedFilterValues) => {
    setQueryParams({
      startDate: newFilter.startDate,
      endDate: newFilter.endDate,
      groupIds: newFilter.groupIds?.join(","),
    });
  };

  const feedTypes: typeof ALL_COMMUNITY_FEED_ITEM_TYPES = useMemo(() => {
    if (activeTab === "deleted") {
      return ["messages", "polls"];
    }

    return ALL_COMMUNITY_FEED_ITEM_TYPES;
  }, [activeTab]);

  const query: ApiQueryParams<"getMessagesV2"> = {
    Tab: activeTab,
    FeedTypes: feedTypes,
    Query: debouncedQuery,
    StartDate: currFilter.startDate ? formatDateToDateTime(currFilter.startDate, false) : undefined,
    EndDate: currFilter.endDate ? formatDateToDateTime(currFilter.endDate, true) : undefined,
    Connection: "included",
    GroupIds: currFilter.groupIds,
  } satisfies ApiQueryParams<"getCommunityFeedV2">;
  const {
    data: messageData,
    hasNextPage: hasMoreMessages,
    isFetchedAfterMount: hasFetchedMessagesAfterMount,
    isRefetching: isRefetchingMessages,
    fetchNextPage: fetchMoreMessages,
    isPending: isPendingMessages,
    isFetchingNextPage: isLoadingMoreMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useInfiniteQuery(communityFeedQueries.messagesInfinite(query));
  // Make an initial request to get total count for scheduled posts
  const { data: scheduledMessages } = useQuery(
    communityFeedQueries.listPosts({
      ...query,
      Tab: "scheduled",
      Limit: 1,
    }),
  );

  function refetchFeed() {
    void refetchMessages();
  }

  const queryArgs = {
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;

  const {
    data: groups = [],
    isPending: isLoadingGroups,
    error: groupsError,
  } = useQuery(communityGroupQueries.list(queryArgs));
  const { data: unreadCounts, error: unreadCountsError } = useQuery(communityFeedQueries.messagesUnreadStats());

  const handleChangeTab = (tab: (typeof ALL_COMMUNITY_FEED_TABS)[number]) => {
    setQueryParams({ tab });
  };

  const messages = useMemo(() => messageData?.pages.flatMap((x) => x.items) ?? [], [messageData]);
  const counts = useMemo(() => {
    return {
      all: queryParams.tab === "all" && messageData ? messageData.pages[0].total : 0,
      announcements: queryParams.tab === "announcements" && messageData ? messageData.pages[0].total : 0,
      myFeed: queryParams.tab === "myFeed" && messageData ? messageData.pages[0].total : 0,
      own: queryParams.tab === "own" && messageData ? messageData.pages[0].total : 0,
      question: queryParams.tab === "question" && messageData ? messageData.pages[0].total : 0,
      scheduled: scheduledMessages ? scheduledMessages.total : 0,
      deleted: queryParams.tab === "deleted" && messageData ? messageData.pages[0].total : 0,
    };
  }, [queryParams.tab, messageData, scheduledMessages]);

  const error = messagesError || groupsError || unreadCountsError || undefined;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const feedProps: CommunityFeedProps = {
    search,
    onChangeSearch: setSearch,
    selectedTab: activeTab,
    onChangeTab: handleChangeTab,
    sessionUser,
    filterOptions: {
      groups: groups.filter((currGroup) => currGroup.type === "interest" || currGroup.type === "realEstate"),
      helpCategories: groups.filter((currGroup) => currGroup.type === "helpCategory"),
    },
    isLoadingGroups,
    unreadCounts,
    counts,
    messages,
    hasMoreMessages,
    fetchMoreMessages,
    refetchMessages: refetchFeed,
    isLoadingMessages: isPendingMessages || (isRefetchingMessages && !hasFetchedMessagesAfterMount),
    isLoadingMoreMessages,
    filter: currFilter,
    onUpdateFilter: handleUpdateFilter,
  };

  return <CommunityFeed {...feedProps} />;
}

function formatDateToDateTime(dateStr: string, endOfDay: boolean) {
  const date = parse(dateStr, "yyyy-MM-dd", new Date());
  date.setHours(endOfDay ? 23 : 0);
  date.setMinutes(endOfDay ? 59 : 0);
  date.setSeconds(endOfDay ? 59 : 0);
  date.setMilliseconds(0);

  return formatISO(date);
}
